import React, { useEffect, useState } from "react";
import { payments as loadSquarePayments } from "@square/web-sdk";
import type {
  ApplePay,
  GooglePay,
  PaymentRequestOptions,
  Payments,
  TokenResult,
} from "@square/web-sdk";
import { usePayment } from "../contexts/PaymentContext";
import { sendAdminAlert } from "../api/admin";

/**
 * Apple Pay / Google Pay buttons built directly on @square/web-sdk.
 *
 * These replace the react-square-web-payments-sdk <ApplePay/> / <GooglePay/>
 * components, which had two failure modes that produced the "pressed Apple Pay
 * and it spun forever" support report:
 *
 *  1. They swallowed every wallet failure into console.error — the customer
 *     (and we) never saw WHY a wallet payment died. Here every non-cancel
 *     failure is shown to the buyer and emailed to admin.
 *  2. Their PaymentForm snapshots createPaymentRequest() ONCE at mount, so any
 *     later total change (promo code applied on the payment step) left the
 *     wallet sheet authorizing a stale amount. Wallet tokens are bound to the
 *     sheet amount, so a stale sheet breaks the payment. Here the wallet is
 *     re-initialized whenever the payment request contents change.
 *
 * Deliberately NO verifyBuyer for wallet tokens: Apple Pay / Google Pay carry
 * their own strong customer authentication (biometric + network cryptogram),
 * Square treats the verification token as optional for US merchants, and the
 * separate post-tokenize verifyBuyer step is exactly what Square's forums
 * report hanging/failing for wallets on iOS 26 Safari. Card payments keep
 * their verifyBuyer flow (see PaymentForm in PaymentFormPage).
 */

const GOOGLE_PAY_CONTAINER_ID = "wallet-google-pay-button";

type WalletButtonsProps = {
  applicationId: string;
  locationId: string;
  /** Wallet sheet contents (line items, shipping, tax, total). */
  paymentRequest: PaymentRequestOptions;
  /** Receives a successful wallet token; runs the order + payment flow. */
  onToken: (token: TokenResult) => Promise<void> | void;
  disabled?: boolean;
  /**
   * Fires with true once at least one wallet button is actually showing, and
   * false while none is.
   *
   * Both buttons render with `display: none` until their wallet initializes,
   * and on a browser with no wallet at all (Firefox, Chrome with nothing in
   * Google Pay) neither ever does. The parent labels this block "Express
   * checkout" and closes it with an "or pay with card" divider, and a heading
   * over an empty box is worse than no heading — so it needs to know.
   */
  onAvailabilityChange?: (available: boolean) => void;
};

const WalletButtons: React.FC<WalletButtonsProps> = ({
  applicationId,
  locationId,
  paymentRequest,
  onToken,
  disabled = false,
  onAvailabilityChange,
}) => {
  const { setErrorMessage } = usePayment();
  const [applePay, setApplePay] = useState<ApplePay | null>(null);
  const [googlePay, setGooglePay] = useState<GooglePay | null>(null);
  const [busy, setBusy] = useState<boolean>(false);

  // Key the init effect on the request CONTENTS, not the object identity —
  // the parent recreates the options object every render.
  const requestJson = JSON.stringify(paymentRequest);

  useEffect(() => {
    let cancelled = false;
    let apple: ApplePay | undefined;
    let google: GooglePay | undefined;

    const init = async () => {
      let payments: Payments | null = null;
      try {
        payments = await loadSquarePayments(applicationId, locationId);
      } catch (error) {
        console.error("Square payments init failed for wallets:", error);
      }
      if (!payments || cancelled) return;

      const options: PaymentRequestOptions = JSON.parse(requestJson);

      // Apple Pay — only supported in Safari on Apple devices with a card in
      // Wallet; anywhere else this rejects and the button stays hidden.
      try {
        apple = await payments.applePay(payments.paymentRequest(options));
        if (!cancelled) setApplePay(apple);
      } catch (error) {
        console.info("Apple Pay unavailable:", error);
      }

      // Google Pay — attach() injects Google's own button into the container.
      try {
        // Clear any leftover button element from a prior attach() call (e.g.
        // after a promo code changes the total and re-triggers this effect)
        // before injecting a new one, to prevent duplicate buttons.
        const gpContainer = document.getElementById(GOOGLE_PAY_CONTAINER_ID);
        if (gpContainer) gpContainer.innerHTML = "";

        google = await payments.googlePay(payments.paymentRequest(options));
        await google.attach(`#${GOOGLE_PAY_CONTAINER_ID}`, {
          buttonColor: "black",
          buttonSizeMode: "fill",
          buttonType: "long",
        });
        if (!cancelled) setGooglePay(google);
      } catch (error) {
        console.info("Google Pay unavailable:", error);
      }
    };

    init();

    return () => {
      cancelled = true;
      setApplePay(null);
      setGooglePay(null);
      apple?.destroy().catch(() => {});
      google?.destroy().catch(() => {});
    };
  }, [applicationId, locationId, requestJson]);

  // Report availability to the parent so it can show (or hide) the "Express
  // checkout" heading and divider that frame these buttons. Kept in its own
  // effect rather than called from init(): the init effect re-runs on every
  // total change and its cleanup nulls both wallets, so calling from there
  // would flap the heading off and on again mid-checkout.
  const isAvailable = !!applePay || !!googlePay;
  useEffect(() => {
    onAvailabilityChange?.(isAvailable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAvailable]);

  const handleWalletClick = async (
    wallet: ApplePay | GooglePay | null,
    label: "Apple Pay" | "Google Pay",
  ) => {
    if (!wallet || busy || disabled) return;
    setBusy(true);
    setErrorMessage(null);
    try {
      const result = await wallet.tokenize();
      if (result.status === "OK") {
        await onToken(result);
      } else if (result.status !== "Cancel") {
        // A real wallet failure (not the buyer closing the sheet). Surface it —
        // silence here is what made past wallet failures undiagnosable.
        const detail = result.errors
          ? (result.errors as { message?: string }[])
              .map((e) => e.message)
              .filter(Boolean)
              .join(". ")
          : `status ${result.status}`;
        console.error(`${label} tokenization failed:`, result);
        setErrorMessage(
          `${label} could not complete and you have NOT been charged. Please try again or pay by card.`,
        );
        void sendAdminAlert(
          `⚠️ ${label} Tokenization Failed`,
          `A customer's ${label} attempt failed in the wallet sheet before any order was created.`,
          { Status: String(result.status), Detail: detail },
        );
      }
    } catch (error: any) {
      console.error(`${label} payment threw:`, error);
      setErrorMessage(
        `${label} could not complete and you have NOT been charged. Please try again or pay by card.`,
      );
      void sendAdminAlert(
        `⚠️ ${label} Payment Threw`,
        `A customer's ${label} attempt threw before any order was created.`,
        { Error: String(error?.message ?? error) },
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Apple Pay: Safari renders the native black button via
          -webkit-appearance (see .apple-pay-button in App.css). */}
      <button
        type="button"
        aria-label="Pay with Apple Pay"
        className="apple-pay-button"
        style={{ display: applePay ? "block" : "none" }}
        disabled={busy || disabled}
        onClick={() => handleWalletClick(applePay, "Apple Pay")}
      />
      {/* Google Pay: Google's button is injected into this container. */}
      <div
        id={GOOGLE_PAY_CONTAINER_ID}
        aria-label="Pay with Google Pay"
        style={{
          display: googlePay ? "block" : "none",
          opacity: busy || disabled ? 0.6 : 1,
          pointerEvents: busy || disabled ? "none" : "auto",
        }}
        onClick={() => handleWalletClick(googlePay, "Google Pay")}
      />
    </>
  );
};

export default WalletButtons;
